import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import PropTypes from 'prop-types'
import Icon from '../Icon/Icon'
import styles from './SiteToast.module.scss'
import {
  SITE_TOAST_MOBILE_BREAKPOINT_PX,
  SITE_TOAST_TOP_OFFSET_DESKTOP_PX,
  SITE_TOAST_TOP_OFFSET_MOBILE_PX,
  SITE_TOAST_TRANSITION_MS,
  SITE_TOAST_VISIBLE_DURATION_MS,
} from './siteToastConstants'

const SiteToastDispatchContext = createContext(null)
const SiteToastStateContext = createContext(null)

const join = (...parts) => parts.filter(Boolean).join(' ')

function readPrefersReducedMotion() {
  return (
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
}

function useResponsiveToastTopPx() {
  const [top, setTop] = useState(SITE_TOAST_TOP_OFFSET_DESKTOP_PX)

  useLayoutEffect(() => {
    const mq =
      typeof window.matchMedia === 'function'
        ? window.matchMedia(`(max-width: ${SITE_TOAST_MOBILE_BREAKPOINT_PX}px)`)
        : null
    const sync = () => {
      if (!mq) {
        setTop(SITE_TOAST_TOP_OFFSET_DESKTOP_PX)
        return
      }
      setTop(mq.matches ? SITE_TOAST_TOP_OFFSET_MOBILE_PX : SITE_TOAST_TOP_OFFSET_DESKTOP_PX)
    }
    sync()
    mq?.addEventListener('change', sync)
    return () => mq?.removeEventListener('change', sync)
  }, [])

  return top
}

function SiteToastRail({ payload, onDismissed }) {
  const topPx = useResponsiveToastTopPx()
  const reducedMotionCssVar = readPrefersReducedMotion() ? '0ms' : `${SITE_TOAST_TRANSITION_MS}ms`

  const [entered, setEntered] = useState(false)
  const [exiting, setExiting] = useState(false)
  const panelRef = useRef(null)
  const timeoutsRef = useRef([])
  const dismissedRef = useRef(false)

  const clearTimers = () => {
    timeoutsRef.current.forEach((id) => clearTimeout(id))
    timeoutsRef.current = []
  }

  const finish = useCallback(() => {
    if (dismissedRef.current) {
      return
    }
    dismissedRef.current = true
    onDismissed()
  }, [onDismissed])

  useLayoutEffect(() => {
    if (!payload) {
      return undefined
    }

    dismissedRef.current = false
    clearTimers()
    setEntered(false)
    setExiting(false)

    const reduced = readPrefersReducedMotion()

    let raf1 = null
    let raf2 = null

    const armExitTimer = () => {
      const waitMs = reduced
        ? SITE_TOAST_VISIBLE_DURATION_MS
        : SITE_TOAST_TRANSITION_MS + SITE_TOAST_VISIBLE_DURATION_MS
      const tid = window.setTimeout(() => {
        setExiting(true)
      }, waitMs)
      timeoutsRef.current.push(tid)
    }

    if (reduced) {
      setEntered(true)
      armExitTimer()
    } else {
      raf1 = window.requestAnimationFrame(() => {
        raf2 = window.requestAnimationFrame(() => {
          setEntered(true)
          armExitTimer()
        })
      })
    }

    return () => {
      if (raf1 != null) window.cancelAnimationFrame(raf1)
      if (raf2 != null) window.cancelAnimationFrame(raf2)
      clearTimers()
    }
  }, [payload.id])

  useEffect(() => {
    if (!exiting) {
      return undefined
    }
    const reduced = readPrefersReducedMotion()
    const ms = reduced ? 200 : SITE_TOAST_TRANSITION_MS + 120
    const tid = window.setTimeout(() => {
      finish()
    }, ms)
    return () => clearTimeout(tid)
  }, [exiting, finish])

  const handleTransitionEnd = (e) => {
    if (!exiting || e.target !== panelRef.current) {
      return
    }
    if (e.propertyName !== 'transform' && e.propertyName !== 'opacity') {
      return
    }
    finish()
  }

  const { message, icon, iconWeight, theme = 'default' } = payload
  const isErrorTheme = theme === 'error'

  return (
    <div className={styles.viewport}>
      <div
        ref={panelRef}
        role={isErrorTheme ? 'alert' : 'status'}
        aria-live={isErrorTheme ? 'assertive' : 'polite'}
        className={join(
          styles.panel,
          isErrorTheme ? styles.panelError : styles.panelDefault,
          entered && !exiting && styles.visible
        )}
        style={{
          top: topPx,
          '--site-toast-duration': reducedMotionCssVar,
        }}
        onTransitionEnd={handleTransitionEnd}
      >
        <div className={styles.body}>
          {icon ? (
            <span className={styles.iconWrap} aria-hidden>
              <Icon name={icon} weight={iconWeight || 'regular'} />
            </span>
          ) : null}
          <span className={styles.text}>{message}</span>
        </div>
      </div>
    </div>
  )
}

SiteToastRail.propTypes = {
  payload: PropTypes.shape({
    id: PropTypes.string.isRequired,
    message: PropTypes.string.isRequired,
    icon: PropTypes.string,
    iconWeight: PropTypes.string,
    theme: PropTypes.oneOf(['default', 'error']),
  }).isRequired,
  onDismissed: PropTypes.func.isRequired,
}

export function SiteToastProvider({ children }) {
  const [toast, setToast] = useState(null)

  const dismissSiteToast = useCallback(() => {
    setToast(null)
  }, [])

  const showSiteToast = useCallback(({ message, icon = null, iconWeight, theme = 'default' }) => {
    if (message == null || String(message).trim() === '') {
      return
    }
    setToast({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      message: String(message),
      icon: icon || null,
      iconWeight: iconWeight || undefined,
      theme: theme === 'error' ? 'error' : 'default',
    })
  }, [])

  const dispatchValue = useMemo(
    () => ({
      showSiteToast,
      dismissSiteToast,
    }),
    [dismissSiteToast, showSiteToast]
  )

  return (
    <SiteToastDispatchContext.Provider value={dispatchValue}>
      <SiteToastStateContext.Provider value={toast}>
        {children}
        {toast ? <SiteToastRail payload={toast} onDismissed={dismissSiteToast} /> : null}
      </SiteToastStateContext.Provider>
    </SiteToastDispatchContext.Provider>
  )
}

SiteToastProvider.propTypes = {
  children: PropTypes.node,
}

SiteToastProvider.defaultProps = {
  children: null,
}

export function useSiteToast() {
  const ctx = useContext(SiteToastDispatchContext)
  if (!ctx) {
    throw new Error('useSiteToast must be used within SiteToastProvider')
  }
  return ctx
}

export function useSiteToastPayload() {
  return useContext(SiteToastStateContext)
}
